import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import CsvInput from "./CsvInput";

const PUBLISH = ["draft", "review", "published"];

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-[0.2em] text-gray-500 block mb-2">{label}</span>
      {children}
    </label>
  );
}

const empty = {
  slug: "", name: "", emirate: "Dubai", city: "Dubai", sub_community: "",
  tagline: "", intro: "", description: "", hero_image: "", lifestyle: "", connectivity: "",
  schools: [], hospitals: [], metro_access: [], bus_access: [], malls: [],
  beaches: [], parks: [], landmarks: [], amenities: [], gallery: [],
  average_price_apartment: "", average_price_villa: "", average_rent_apartment: "",
  rental_yield: "", capital_appreciation: "",
  investment_score: "", family_score: "", luxury_score: "",
  investment_analysis: "",
  latitude: "", longitude: "",
  seo_title: "", seo_description: "", faqs: [], publish_status: "published", featured: false,
};

export default function AdminCommunityForm() {
  const { slug } = useParams();
  const isEdit = Boolean(slug);
  const navigate = useNavigate();
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isEdit) {
      api.get(`/communities/${slug}`, { params: { raw: true } }).then((r) => {
        const d = r.data;
        // Legacy shape: schools may be a string
        const arr = (v) => (Array.isArray(v) ? v : v ? [v] : []);
        setForm({
          ...empty, ...d,
          schools: arr(d.schools), hospitals: arr(d.hospitals),
          metro_access: arr(d.metro_access), bus_access: arr(d.bus_access),
          malls: arr(d.malls), beaches: arr(d.beaches), parks: arr(d.parks),
          landmarks: arr(d.landmarks),
          amenities: d.amenities || [], gallery: d.gallery || [],
          average_price_apartment: d.average_price_apartment ?? "",
          average_price_villa: d.average_price_villa ?? "",
          average_rent_apartment: d.average_rent_apartment ?? "",
          rental_yield: d.rental_yield ?? "",
          capital_appreciation: d.capital_appreciation ?? "",
          investment_score: d.investment_score ?? "",
          family_score: d.family_score ?? "",
          luxury_score: d.luxury_score ?? "",
          latitude: d.latitude ?? "", longitude: d.longitude ?? "",
        });
      });
    }
  }, [slug, isEdit]);

  const update = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const num = (v) => (v === "" || v == null ? null : Number(v));
      const cleanSlug = (form.slug || form.name).toString().trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
      const payload = {
        ...form,
        slug: cleanSlug,
        name: (form.name || "").trim(),
        average_price_apartment: num(form.average_price_apartment),
        average_price_villa: num(form.average_price_villa),
        average_rent_apartment: num(form.average_rent_apartment),
        rental_yield: num(form.rental_yield),
        capital_appreciation: num(form.capital_appreciation),
        investment_score: num(form.investment_score),
        family_score: num(form.family_score),
        luxury_score: num(form.luxury_score),
        latitude: num(form.latitude), longitude: num(form.longitude),
      };
      if (isEdit) { await api.put(`/admin/communities/${slug}`, payload); toast.success("Community saved"); }
      else { await api.post(`/admin/communities`, payload); toast.success("Community created"); }
      navigate("/admin/communities");
    } catch (err) {
      // FastAPI validation errors come back as { detail: [ { loc, msg, ... }, ... ] }.
      // Rendering that object directly in a toast crashed React with
      // "Objects are not valid as a React child", so we always coerce to string.
      const raw = err?.response?.data?.detail;
      let msg = "Save failed";
      if (typeof raw === "string") msg = raw;
      else if (Array.isArray(raw)) msg = raw.map((e) => `${(e.loc || []).slice(-1)}: ${e.msg}`).join(" · ");
      toast.error(msg);
    } finally { setSaving(false); }
  };

  const submitWithStatus = async (status) => {
    // Set publish_status then flush via the normal submit path so validation
    // and toast handling stay identical between "Save & Publish" / "Save draft".
    setForm((s) => ({ ...s, publish_status: status }));
    // Wait for state to flush by using a microtask trick: pass the value
    // directly to a helper that skips the state read.
    await new Promise((r) => setTimeout(r, 0));
    setSaving(true);
    try {
      const num = (v) => (v === "" || v == null ? null : Number(v));
      const cleanSlug = (form.slug || form.name).toString().trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
      const payload = {
        ...form,
        publish_status: status,
        slug: cleanSlug,
        name: (form.name || "").trim(),
        average_price_apartment: num(form.average_price_apartment),
        average_price_villa: num(form.average_price_villa),
        average_rent_apartment: num(form.average_rent_apartment),
        rental_yield: num(form.rental_yield),
        capital_appreciation: num(form.capital_appreciation),
        investment_score: num(form.investment_score),
        family_score: num(form.family_score),
        luxury_score: num(form.luxury_score),
        latitude: num(form.latitude), longitude: num(form.longitude),
      };
      if (isEdit) {
        await api.put(`/admin/communities/${slug}`, payload);
        toast.success(status === "published" ? "Community published" : "Draft saved");
      } else {
        await api.post(`/admin/communities`, payload);
        toast.success(status === "published" ? "Community published" : "Draft created");
      }
      navigate("/admin/communities");
    } catch (err) {
      const raw = err?.response?.data?.detail;
      let msg = "Save failed";
      if (typeof raw === "string") msg = raw;
      else if (Array.isArray(raw)) msg = raw.map((e) => `${(e.loc || []).slice(-1)}: ${e.msg}`).join(" · ");
      toast.error(msg);
    } finally { setSaving(false); }
  };

  const inputCls = "w-full border border-[#E5E7EB] px-3 py-2 text-sm text-gray-900 bg-white focus:outline-none focus:border-[#001588]";

  const statusPill = form.publish_status === "published"
    ? { label: "Live", cls: "bg-green-100 text-green-800 border-green-300" }
    : form.publish_status === "review"
      ? { label: "Under review", cls: "bg-amber-100 text-amber-800 border-amber-300" }
      : { label: "Draft", cls: "bg-neutral-100 text-neutral-700 border-neutral-300" };

  return (
    <form onSubmit={(e) => { e.preventDefault(); submitWithStatus(form.publish_status || "published"); }} className="p-10 max-w-5xl" data-testid="admin-community-form">
      <div className="mb-8 flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="text-xs tracking-[0.24em] uppercase text-gray-500 mb-1">UAE database</div>
          <div className="flex items-center gap-3">
            <h1 className="font-serif text-4xl">{isEdit ? "Edit community" : "New community"}</h1>
            <span className={`text-xs uppercase tracking-[0.15em] px-2.5 py-1 border ${statusPill.cls}`} data-testid="community-status-pill">
              {statusPill.label}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button type="button" onClick={() => submitWithStatus("draft")} disabled={saving} data-testid="community-save-draft-btn" className="px-5 py-3 border border-[#001588] text-[#001588] text-sm uppercase tracking-[0.2em] hover:bg-[#001588] hover:text-white transition-colors disabled:opacity-60">
            {saving ? "Saving…" : "Save as draft"}
          </button>
          <button type="button" onClick={() => submitWithStatus("published")} disabled={saving} data-testid="community-publish-btn" className="px-6 py-3 bg-[#001588] text-white text-sm uppercase tracking-[0.2em] hover:bg-black disabled:opacity-60">
            {saving ? "Publishing…" : form.publish_status === "published" && isEdit ? "Update live" : "Publish"}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 bg-white border border-[#E5E7EB] p-8">
        <Field label="Community name *"><input required data-testid="community-input-name" className={inputCls} value={form.name} onChange={(e) => update("name", e.target.value)} /></Field>
        <Field label="Slug"><input className={inputCls} value={form.slug} onChange={(e) => update("slug", e.target.value)} disabled={isEdit} /></Field>
        <Field label="Emirate"><input className={inputCls} value={form.emirate} onChange={(e) => update("emirate", e.target.value)} /></Field>
        <Field label="City"><input className={inputCls} value={form.city} onChange={(e) => update("city", e.target.value)} /></Field>
        <Field label="Sub-community"><input className={inputCls} value={form.sub_community || ""} onChange={(e) => update("sub_community", e.target.value)} /></Field>
        <Field label="Tagline"><input className={inputCls} value={form.tagline || ""} onChange={(e) => update("tagline", e.target.value)} /></Field>

        <div className="md:col-span-2">
          <Field label="Intro (top paragraph on public page)">
            <textarea rows={5} className={inputCls} value={form.intro || ""} onChange={(e) => update("intro", e.target.value)} data-testid="community-input-intro" placeholder="The paragraph visitors see at the top of /areas/<slug>. Write in Point Penta's voice, 80–150 words." />
          </Field>
        </div>
        <div className="md:col-span-2">
          <Field label="Lifestyle">
            <textarea rows={3} className={inputCls} value={form.lifestyle || ""} onChange={(e) => update("lifestyle", e.target.value)} />
          </Field>
        </div>

        <Field label="Hero image URL"><input className={inputCls} value={form.hero_image || ""} onChange={(e) => update("hero_image", e.target.value)} /></Field>
        <Field label="Connectivity"><input className={inputCls} value={form.connectivity || ""} onChange={(e) => update("connectivity", e.target.value)} /></Field>

        <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-6">
          <Field label="Nearby schools (comma-separated)"><CsvInput className={inputCls} value={form.schools} onChange={(v) => update("schools", v)} data-testid="community-input-schools" /></Field>
          <Field label="Hospitals (comma-separated)"><CsvInput className={inputCls} value={form.hospitals} onChange={(v) => update("hospitals", v)} data-testid="community-input-hospitals" /></Field>
          <Field label="Metro access (comma-separated)"><CsvInput className={inputCls} value={form.metro_access} onChange={(v) => update("metro_access", v)} data-testid="community-input-metro" /></Field>
          <Field label="Bus access (comma-separated)"><CsvInput className={inputCls} value={form.bus_access} onChange={(v) => update("bus_access", v)} data-testid="community-input-bus" /></Field>
          <Field label="Malls (comma-separated)"><CsvInput className={inputCls} value={form.malls} onChange={(v) => update("malls", v)} data-testid="community-input-malls" /></Field>
          <Field label="Beaches (comma-separated)"><CsvInput className={inputCls} value={form.beaches} onChange={(v) => update("beaches", v)} data-testid="community-input-beaches" /></Field>
          <Field label="Parks (comma-separated)"><CsvInput className={inputCls} value={form.parks} onChange={(v) => update("parks", v)} data-testid="community-input-parks" /></Field>
          <Field label="Landmarks (comma-separated)"><CsvInput className={inputCls} value={form.landmarks} onChange={(v) => update("landmarks", v)} data-testid="community-input-landmarks" /></Field>
          <Field label="Amenities (comma-separated)"><CsvInput className={inputCls} value={form.amenities} onChange={(v) => update("amenities", v)} data-testid="community-input-amenities" /></Field>
        </div>

        <Field label="Avg apartment price (AED / sqft)"><input type="number" className={inputCls} value={form.average_price_apartment} onChange={(e) => update("average_price_apartment", e.target.value)} /></Field>
        <Field label="Avg villa price (AED / sqft)"><input type="number" className={inputCls} value={form.average_price_villa} onChange={(e) => update("average_price_villa", e.target.value)} /></Field>
        <Field label="Rental yield (%)"><input type="number" step="0.1" className={inputCls} value={form.rental_yield} onChange={(e) => update("rental_yield", e.target.value)} /></Field>
        <Field label="Capital appreciation (% YoY)"><input type="number" step="0.1" className={inputCls} value={form.capital_appreciation} onChange={(e) => update("capital_appreciation", e.target.value)} /></Field>
        <Field label="Avg apartment rent (AED / year)"><input type="number" className={inputCls} value={form.average_rent_apartment} onChange={(e) => update("average_rent_apartment", e.target.value)} /></Field>

        <Field label="Investment score (0-10)"><input type="number" step="0.1" className={inputCls} value={form.investment_score} onChange={(e) => update("investment_score", e.target.value)} /></Field>
        <Field label="Family score (0-10)"><input type="number" step="0.1" className={inputCls} value={form.family_score} onChange={(e) => update("family_score", e.target.value)} /></Field>
        <Field label="Luxury score (0-10)"><input type="number" step="0.1" className={inputCls} value={form.luxury_score} onChange={(e) => update("luxury_score", e.target.value)} /></Field>

        <Field label="Latitude"><input type="number" step="0.0001" className={inputCls} data-testid="community-input-lat" value={form.latitude} onChange={(e) => update("latitude", e.target.value)} /></Field>
        <Field label="Longitude"><input type="number" step="0.0001" className={inputCls} data-testid="community-input-lng" value={form.longitude} onChange={(e) => update("longitude", e.target.value)} /></Field>

        <div className="md:col-span-2">
          <Field label="Gallery image URLs (comma-separated)"><CsvInput className={inputCls} value={form.gallery} onChange={(v) => update("gallery", v)} data-testid="community-input-gallery" /></Field>
        </div>

        <Field label="SEO title"><input className={inputCls} value={form.seo_title || ""} onChange={(e) => update("seo_title", e.target.value)} /></Field>
        <Field label="SEO description"><input className={inputCls} value={form.seo_description || ""} onChange={(e) => update("seo_description", e.target.value)} /></Field>

        <Field label="Publish status">
          <select className={inputCls} value={form.publish_status} onChange={(e) => update("publish_status", e.target.value)}>
            {PUBLISH.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </Field>

        <label className="flex items-center gap-2 pt-4">
          <input type="checkbox" checked={!!form.featured} onChange={(e) => update("featured", e.target.checked)} data-testid="community-featured" />
          <span className="text-sm text-gray-700"><strong>Featured on Home page</strong> · surfaces this community in the &ldquo;Five neighbourhoods&rdquo; section on <code>/</code></span>
        </label>
      </div>
    </form>
  );
}
